import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { Calendar, X, Filter, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const PaymentTransactionsContent = () => {

  const { businessEntityId: business_entity_id } = JSON.parse(sessionStorage.getItem("userData"));

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [paymentType, setPaymentType] = useState('all');
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  function getCurrentDate() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let url = `hr/payment-transactions/${business_entity_id}?start_date=${startDate}&end_date=${endDate}`;

      if (paymentType !== 'all') {
        url += `&payment_type=${paymentType}`;
      }

      const response = await axios.get(url);
      setTransactions(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transaction data. Please try again later.');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = () => {
    fetchTransactions();
    setShowFilterModal(false);
  };

  const handleExportCSV = () => {
    // Create CSV content
    const headers = ["Date", "Employee", "Role", "Base Amount", "Advance Payment",
      "Additional Payment", "Bonus", "Status", "Final Payment", "Working Days"];

    const csvContent = [
      headers.join(','),
      ...transactions.map(t => [
        new Date(t.date).toLocaleDateString(),
        t.full_name,
        t.role,
        parseFloat(t.base_amount).toFixed(2),
        parseFloat(t.advance_payment).toFixed(2),
        parseFloat(t.adding_payment).toFixed(2),
        parseFloat(t.bonus || 0).toFixed(2),
        t.status,
        parseFloat(t.final_payment).toFixed(2),
        t.total_working_days
      ].join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `payment_transactions_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = transactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(transactions.length / itemsPerPage);

  const renderTableRows = () => {
    if (currentTransactions.length === 0) {
      return (
        <tr>
          <td colSpan="10" className="px-6 py-10 text-center text-gray-500">
            No transactions found for the selected period.
          </td>
        </tr>
      );
    }

    return currentTransactions.map((transaction) => (
      <tr key={transaction.id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {new Date(transaction.date).toLocaleDateString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">{transaction.full_name}</div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {transaction.role}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ${parseFloat(transaction.base_amount).toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ${parseFloat(transaction.advance_payment).toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ${parseFloat(transaction.adding_payment).toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ${parseFloat(transaction.bonus || 0).toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 text-xs rounded-full ${transaction.status === 'Full Payment'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
            }`}>
            {transaction.status}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
          ${parseFloat(transaction.final_payment).toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {transaction.total_working_days}
        </td>
      </tr>
    ));
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Payment Transactions</h2>
        <div className="flex space-x-2">
          <button
            className="px-4 py-2 flex items-center space-x-2 bg-white border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setShowFilterModal(true)}
          >
            <Filter className="h-4 w-4" />
            <span>Filter</span>
          </button>
          <button
            className="px-4 py-2 flex items-center space-x-2 bg-green-600 rounded-lg text-sm text-white hover:bg-green-700"
            onClick={handleExportCSV}
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing transactions from <span className="font-medium">{startDate}</span> to <span className="font-medium">{endDate}</span>
            {paymentType !== 'all' && (
              <span> • {paymentType === 'daily' ? 'Daily Workers' : 'Monthly Workers'}</span>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-600">Total: {transactions.length} transactions</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 m-4 rounded-lg">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center p-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Base Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Advance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Additional
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bonus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Final Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Working Days
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderTableRows()}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {transactions.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, transactions.length)}
                  </span>{" "}
                  of <span className="font-medium">{transactions.length}</span> results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${page === currentPage
                          ? 'bg-blue-600 text-white focus:z-20'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Filter Transactions</h3>
              <button onClick={() => setShowFilterModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className={`py-2 rounded-lg text-center text-sm ${paymentType === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                      }`}
                    onClick={() => setPaymentType('all')}
                  >
                    All
                  </button>
                  <button
                    className={`py-2 rounded-lg text-center text-sm ${paymentType === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                      }`}
                    onClick={() => setPaymentType('daily')}
                  >
                    Daily
                  </button>
                  <button
                    className={`py-2 rounded-lg text-center text-sm ${paymentType === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                      }`}
                    onClick={() => setPaymentType('monthly')}
                  >
                    Monthly
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-2">
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFilterSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentTransactionsContent;